import { i18n } from "@/i18n";
import { formManager } from "@/services/form/form-manager";
import { tabsManager } from "@/services/utilities/tabs-manager";
import { dateFormatter } from "@/services/utilities/date-formatter";

const EmailRegex =
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
const FloatRegex = /^[+-]?([0-9]*[.|,])?[0-9]+$/;
const PasswordRegex = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z\d]).{12,}$/;

class FormValidator {
  validEmail(email) {
    return EmailRegex.test(email);
  }

  validNumberOrFloat(value) {
    return FloatRegex.test(value);
  }

  validMark(value) {
    return FloatRegex.test(value);
  }

  validPassword(value, model = null) {
    const attrRegex = model?.vars?.attr?.regex;
    const regex = attrRegex ? this.parseRegexAttr(attrRegex) : PasswordRegex;
    return regex.test(value);
  }

  parseRegexAttr(regexString) {
    try {
      const match = String(regexString).match(/^\/(.*)\/([a-z]*)$/);
      if (!match) {
        return new RegExp(regexString);
      }
      return new RegExp(match[1], match[2]);
    } catch (e) {
      console.warn(`Invalid password regex received from back-end, falling back to default: "${regexString}"`, e);
      return PasswordRegex;
    }
  }

  validCheckbox(content) {
    let valid = false;
    Object.values(content).forEach((value) => {
      if (value.choices) {
        Object.values(value.choices).forEach((choice) => {
          if (choice.checked && !valid) {
            valid = true;
          }
        });
      } else {
        if (value.checked && !valid) {
          valid = true;
        }
      }
    });
    return valid;
  }

  validFileExtension(filename, extensionsAccepted) {
    let valid = true;
    let type;
    const typeFromName = filename.match(/(.*)\.(.*)/).slice(-1)[0];
    if (typeFromName) {
      type = "." + filename.match(/(.*)\.(.*)/).slice(-1)[0];
    }
    if (extensionsAccepted && !new RegExp(extensionsAccepted).test(type.toLowerCase())) {
      valid = false;
    }
    return valid;
  }

  validFileSize(size, maxFileSize) {
    let valid = true;
    if (size > maxFileSize * 1024 * 1024) {
      valid = false;
    }
    return valid;
  }

  isVisible(model) {
    const el = document.getElementById(model.vars.id);
    if (!el) {
      model.vars.ignoreValidation = true;
      return;
    }
    const hiddenByToggle = !!el.closest(".d-none");
    const markedIgnore = !!el.closest(".ignore-validation") || el.classList.contains("ignore-validation");
    model.vars.ignoreValidation = hiddenByToggle || markedIgnore;
  }

  validChildren(errors, model, $this, models) {
    Object.values(model.children).forEach((children) => {
      // Recalcule ignoreValidation depuis le DOM (comme validForm) pour couvrir
      // tout masquage — pas seulement le flag posé par checkToggleTarget.
      $this.isVisible(children);
      if (!children.vars.ignoreValidation) {
        errors = errors.concat($this.validModel(children, "", false, models));
      }
    });
    return errors;
  }

  validForm(models, object = false, multipleCheckBox = false, setFocus = true) {
    const $this = this;
    let errors = [];
    Object.values(models).forEach(function (model) {
      if (
        model?.vars?.attr &&
        model.vars.attr["hasChildren"] &&
        model.vars.attr["hasChildren"] === true &&
        Object.keys(model?.children).length
      ) {
        errors = $this.validChildren(errors, model, $this, models);
      }
      if ($this.isCollection(model)) {
        Object.values(model.children).forEach((child) => {
          errors = errors.concat($this.validForm(child.children));
        });
      }
      if (formManager.isFormType(model) && model.children && Object.keys(model.children).length) {
        if ($this.isFormTypeVisible(model)) {
          errors = errors.concat($this.validForm(model.children));
        }
      } else {
        $this.isVisible(model);
        if (
          !formManager.isAlertType(model) &&
          !model.vars.disabled &&
          !model.vars.ignoreValidation &&
          !model.vars.hide
        ) {
          errors = errors.concat($this.validModel(model, "", multipleCheckBox));
        }
      }
    });

    this.focusErrorInput(errors, setFocus);
    if (object) {
      return {
        errors: errors,
        models: models,
      };
    }
    return errors;
  }

  isCollection(model) {
    return model.vars.block_prefixes?.[1] === "collection" && model.vars.id !== "role_permissions";
  }

  isFormTypeVisible(model) {
    const firstChild = Object.values(model.children)[0];
    const el = firstChild ? document.getElementById(firstChild.vars.id) : null;
    return !!(el && (el.offsetHeight || el.getClientRects().length));
  }

  isFormTypeRendered(model) {
    const firstChild = Object.values(model.children)[0];
    const el = firstChild ? document.getElementById(firstChild.vars.id) : null;
    return !!el && !el.closest(".d-none, .ignore-validation");
  }

  // Variante sans effet de bord de validForm : ne pose ni erreur, ni focus, ni ignoreValidation.
  hasMissingRequired(models) {
    return Object.values(models || {}).some((model) => {
      if (!model?.vars) return false;
      const children = Object.values(model.children || {});
      if (model.vars.attr?.hasChildren === true && this.hasMissingRequired(children)) return true;
      if (this.isCollection(model) && children.some((child) => this.hasMissingRequired(child.children))) return true;
      if (formManager.isFormType(model) && children.length) {
        return this.isFormTypeRendered(model) && this.hasMissingRequired(children);
      }
      return this.isRequiredAndEmpty(model);
    });
  }

  isRequiredAndEmpty(model) {
    const { disabled, hide, id, required, value } = model.vars;
    if (disabled || hide || !required) return false;
    const el = document.getElementById(id);
    if (el && el.closest(".d-none, .ignore-validation")) return false;
    return this.isEmpty(value);
  }

  handleRequiredErrors(model, errors = []) {
    if (!model.vars.required) return;

    const isEmpty = this.isEmpty(model.vars.value);

    if (isEmpty) {
      model.vars.isInvalid = true;
      model.vars.error = i18n.global.t("requiredField");
      model.vars.errorType = "required";
      errors.push(model.vars.id);
      return true;
    } else {
      model.vars.isInvalid = false;
      if (model.vars.errorType === "required") {
        delete model.vars.error;
        delete model.vars.errorType;
      }
      return false;
    }
  }

  isEmpty(value) {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "object") return Object.keys(value).length === 0;
    if (typeof value === "boolean") return !value;
    return false;
  }

  validModel(model, language = "", multipleCheckBox = false, models = null) {
    const errorMessage = i18n.global.t("requiredField");
    let error = "";
    let errors = [];

    if (!model) {
      return errors;
    }

    let content = model.translatable ? model.vars.value[language] : model.vars.value;
    const isRequiredError = this.handleRequiredErrors(model, errors);
    if (isRequiredError) {
      return errors;
    }

    if (model.vars.row && model.vars.required && typeof model.vars.value === "object" && !multipleCheckBox) {
      const errorArray = this.validClusterRow(model, errorMessage);
      if (errorArray.length) {
        error = errorArray;
      } else {
        delete model.vars.error;
      }
    } else if (model.vars.value && model.vars.attr && model.vars.attr["regex"] && !formManager.isPasswordType(model)) {
      const regex = this.parseRegexAttr(model.vars.attr["regex"]);
      const valueToTest = model.vars.attr["regex-trim"]
        ? String(model.vars.value).replace(/\s/g, "")
        : model.vars.value;
      const regexResult = regex.test(valueToTest);
      if (!regexResult) {
        error = model.vars.attr["regex-error"];
      } else {
        delete model.vars.error;
      }
    } else if (model.vars.row && model.vars.required && typeof model.vars.value === "object" && multipleCheckBox) {
      if (
        model?.vars?.value &&
        Array.isArray(model?.vars?.value) &&
        !model?.vars?.value.some((element) => element !== "")
      ) {
        const errorArray = this.validClusterRow(model, errorMessage);
        if (errorArray.length) {
          error = errorArray;
        }
      }
    } else if (
      model.vars.required &&
      (formManager.isTextType(model) ||
        formManager.isTextareaType(model) ||
        formManager.isPhoneType(model) ||
        formManager.isEditorType(model)) &&
      (!content || !content.length)
    ) {
      error = errorMessage;
    } else if (formManager.isSliderType(model)) {
      if (model.vars.required && (content === null || content === undefined || content === "")) {
        error = errorMessage;
      }
    } else if (
      model.vars.required &&
      formManager.isRadioType(model) &&
      ((!content && content !== 0) || !content.toString().length)
    ) {
      error = errorMessage;
      // ST143-TA-5304 (revue front !1787) : miroir de la branche radio ci-dessus pour les
      // switch forcés (data-force-switch) — sans impact aujourd'hui (les 2 champs concernés
      // sont required => false), garde-fou pour un futur champ requis de ce type.
    } else if (
      model.vars.required &&
      formManager.isSwitchType(model) &&
      ((!content && content !== 0) || !content.toString().length)
    ) {
      error = errorMessage;
    } else if (formManager.isDateType(model) && content) {
      error = this.handleDateError(model, models);
    } else if (formManager.isNumberType(model)) {
      error = this.handleNumberError(model, models);
    } else if (model.vars.required && formManager.isFileType(model) && (!content || (content && !("id" in content)))) {
      error = errorMessage;
    } else if (model.vars.required && formManager.isCheckboxType(model) && !this.validCheckbox(content)) {
      error = errorMessage;
    } else if (model.vars.required && formManager.isSelectType(model) && (!content || content === "")) {
      error = errorMessage;
    } else if (formManager.isMultiple(model) && formManager.isSelectType(model)) {
      if ((!content || (content && !Object.keys(content).length)) && model.vars.required) {
        error = errorMessage;
      } else if (
        model.vars.attr &&
        model.vars.attr["data-min-tag"] &&
        Object.keys(content).length &&
        Object.keys(content).length < model.vars.attr["data-min-tag"]
      ) {
        error = i18n.global.t("minTagError") + " (" + model.vars.attr["data-min-tag"] + ")";
      } else if (
        model.vars.attr &&
        model.vars.attr["data-max-tag"] &&
        Object.keys(content).length &&
        Object.keys(content).length > model.vars.attr["data-max-tag"]
      ) {
        error = i18n.global.t("maxTagError") + " (" + model.vars.attr["data-max-tag"] + ")";
      } else {
        delete model.vars.error;
      }
    } else if (formManager.isEmailType(model)) {
      if (model.vars.required && !content.length) {
        error = errorMessage;
      } else if (content && content.length && !this.validEmail(content)) {
        error = i18n.global.t("emailError");
      } else {
        delete model.vars.error;
      }
    } else if (formManager.isPasswordType(model)) {
      if (model.vars.required && !content.length) {
        error = errorMessage;
      } else if (content && content.length && model?.vars?.attr?.check && !this.validPassword(content, model)) {
        error = model?.vars?.attr?.["regex-error"] || i18n.global.t("password_not_valid");
      } else {
        delete model.vars.error;
      }
    } else if (formManager.isPhoneType(model) && !this.validPhone(model)) {
      error = i18n.global.t("phoneError");
    } else if (formManager.isIconType(model) && content && !this.validIcon(content, model)) {
      error = i18n.global.t("icon.invalid");
    } else {
      if (model.translatable && model.vars.error) {
        delete model.vars.error[language];
      } else {
        delete model.vars.error;
      }
    }

    let rowIsValid = true;
    if (model.vars.row && error && typeof error === "object" && error.length) {
      Object.values(error).forEach((item) => {
        if (rowIsValid && item !== null) {
          rowIsValid = false;
        }
      });
    }
    if (error) {
      if (model.translatable) {
        model["vars"]["error"][language] = error;
      } else {
        model["vars"]["error"] = error;
      }
      if (!model.vars.row || (model.vars.row && !rowIsValid)) {
        errors.push(model.vars.id);
      }
    }
    return errors;
  }

  validClusterRow(model, errorMessage) {
    let rowErrors = [];
    let index = 0;
    let answered = 0;
    Object.values(model.vars.value).forEach((value) => {
      if (!value || (value && !value.length)) {
        rowErrors[index] = errorMessage;
      } else {
        rowErrors[index] = null;
        answered++;
      }
      index++;
    });

    // ST143-TA-5304 (revue front !1787) : miroir isSwitchType, cohérence avec la branche
    // validate() — un switch requis dans un cluster se comporte comme un radio.
    if (formManager.isRadioType(model) && answered) {
      rowErrors = [];
    } else if (formManager.isSwitchType(model) && answered) {
      rowErrors = [];
    }
    return rowErrors;
  }

  validPhone(model) {
    if (!model.vars.value) return true;
    const regex = /^\+?[0-9\s\-().]{7,}$/;
    return regex.test(model.vars.value);
  }

  validIcon(content, model) {
    const names = model.vars.multiple === true ? content : [content];
    return names.every((name) => formManager.isValidIconName(name));
  }

  validRepeatedPassword(password, repeatedPassword, errors) {
    if (password && repeatedPassword && password?.vars?.value !== repeatedPassword?.vars?.value) {
      repeatedPassword.vars.error = i18n.global.t("second_password_not_valid");
      errors.push(repeatedPassword.vars.id);
    }
    return errors;
  }

  processResponses(responses, models, returnAlerts = false) {
    let errors = [];
    let alerts = [];
    if (responses.length) {
      responses.forEach((error) => {
        const field = formManager.findFieldByName(models, error.field);
        if (field) {
          if (!error.context || (error.context && !error.context.alertId)) {
            field.vars.error = error.message;
            errors = errors.concat(field.vars.id);
            this.clearModelAlerts(field);
            field.vars.isInvalid = false;
          } else {
            const fieldAlert = field.vars.attr.alerts.find((alert) => alert.id === error.context.alertId);
            if (fieldAlert) {
              fieldAlert.isTriggered = true;
              fieldAlert.fieldName = field.vars.label;
              fieldAlert.fieldId = field.vars.id;
              field.vars.isInvalid = fieldAlert.level > 1;
              alerts.push(fieldAlert);
            } else {
              field.vars.isInvalid = false;
            }
          }
        }
      });
    }
    // processResponses() gère l'auto-save champ par champ (saisie en cours, cf.
    // PreRegistrationSheetView.saveField()) : reposer le focus ici arracherait la frappe en
    // cours dès qu'une erreur revient sur le champ qu'on vient de quitter. On reste en scroll
    // seul ; le focus RGAA est réservé aux chemins de soumission explicite (cf. validForm).
    this.focusErrorInput(errors);
    if (returnAlerts) {
      return alerts;
    }
    return errors;
  }

  focusErrorInput(errors, setFocus = false) {
    if (errors.length) {
      const invalidInput = document.getElementById(errors[0]);
      if (invalidInput) {
        const offset = window.innerWidth < 768 ? 300 : 250;
        window.scrollTo({
          behavior: "smooth",
          top: invalidInput.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
        });
        if (setFocus) {
          this.focusInvalidField(invalidInput);
        }
      }
    }
  }

  focusInvalidField(invalidInput) {
    // RGAA 3.3 : focus clavier sur le champ en erreur, en plus du scroll fait dans
    // focusErrorInput(). Les champs à overlay (select vue-multiselect, date flatpickr) ouvrent
    // leur popup au focus et masquent le message d'erreur qu'on veut montrer : on focalise alors
    // leur conteneur (.form-group), rendu focusable via tabindex, plutôt que le champ lui-même.
    // - flatpickr (DateTypeComponent) : l'id du modèle reste sur l'input flatpickr d'origine,
    //   qui devient type="hidden" une fois altInput actif (l'input visible est un sibling sans id).
    // - vue-multiselect (SelectTypeComponent) : l'id du modèle se retrouve sur son input de
    //   recherche interne (class "multiselect__input"), dont le focus déclenche activate().
    // preventScroll évite que le scroll natif du focus (sans offset) n'écrase le scroll
    // offsetté fait juste au-dessus (le champ se retrouverait sous le header sticky).
    const isMultiselectInput = invalidInput.classList.contains("multiselect__input");
    const isTextField =
      ["INPUT", "TEXTAREA"].includes(invalidInput.tagName) && invalidInput.type !== "hidden" && !isMultiselectInput;
    if (isTextField) {
      invalidInput.focus({ preventScroll: true });
      return;
    }
    const container = invalidInput.closest(".form-group") || invalidInput;
    if (!container.hasAttribute("tabindex")) {
      container.setAttribute("tabindex", "-1");
    }
    container.focus({ preventScroll: true });
  }

  validTabs(tabs) {
    let errors = [];
    let valid = false;
    let invalidTab = {};
    Object.values(tabs).forEach(function (tab) {
      if (!errors.length && !tab.hidden && !tab.disabled) {
        errors = errors.concat(formValidator.validForm(tab.models));
        if (errors.length) {
          invalidTab = tab;
        }
      }
    });

    if (Object.keys(invalidTab).length) {
      tabsManager.displayTab(invalidTab);
    } else {
      valid = true;
    }

    return valid;
  }

  handleNumberError(model, models) {
    if (model.vars.block_prefixes[1] !== "number") return;
    const vars = model.vars;
    // Une saisie non numérique dans un input number est vidée par le navigateur : seul `validity.badInput` la révèle.
    if (this.hasBadNumberInput(vars.id)) {
      vars.errorType = "badInput";
      return this.handleError(i18n.global.t("requiredNumberOrFloat"), true, model);
    }
    if (vars.errorType === "badInput") {
      delete vars.errorType;
      this.handleError(null, false, model);
    }
    if (!vars.value || vars.value === "" || vars.block_prefixes[1] !== "number") return;

    if (!this.validNumberOrFloat(vars.value)) {
      return this.handleError(i18n.global.t("requiredNumberOrFloat"), true, model);
    }

    const { attr } = vars;
    const direction = attr?.["data-max-ref"] ? "max" : attr?.["data-min-ref"] ? "min" : null;

    if (direction && models) {
      const modelError = this.validateRefConstraint(model, models, direction);
      if (modelError) return modelError;
    }

    const boundError = this.validateNumberBound(model);
    if (boundError) return boundError;
  }

  validateNumberBound(model) {
    const { attr, value } = model.vars;
    const numberValue = parseFloat(value);

    const bounds = [
      {
        bound: attr?.["data-number-max"],
        inactive: attr?.["data-number-max-inactive"] === true,
        violates: (b) => numberValue > b,
        errorAttr: "data-error-max",
        errorKey: "number_error.number_min_error",
      },
      {
        bound: attr?.["data-number-min"],
        inactive: attr?.["data-number-min-inactive"] === true,
        violates: (b) => numberValue < b,
        errorAttr: "data-error-min",
        errorKey: "number_error.number_max_error",
      },
    ];

    const activeBounds = bounds.filter(({ bound, inactive }) => bound !== undefined && bound !== null && !inactive);
    if (!activeBounds.length) return null;

    for (const { bound, violates, errorAttr, errorKey } of activeBounds) {
      if (violates(parseFloat(bound))) {
        const error = attr[errorAttr] || i18n.global.t(errorKey, { fieldName: bound });
        return this.handleError(error, true, model);
      }
    }

    return this.handleError(null, false, model);
  }

  handleDateError(model, models) {
    const { attr, value } = model.vars;
    const direction = attr?.["data-max-ref"] ? "max" : attr?.["data-min-ref"] ? "min" : null;

    if (!models || !direction || !value) return null;

    if (!dateFormatter.isDate(value)) {
      return i18n.global.t("date_error.date_format_error");
    }

    return this.validateRefConstraint(model, models, direction);
  }

  validateRefConstraint(model, models, direction, shouldNoBeCalledOnRef = false) {
    const inverseDirection = direction === "max" ? "min" : "max";
    const modelValue = model.vars.value;
    if (!modelValue) return null;

    const rawRefs = model.vars.attr?.["data-" + direction + "-ref"];
    if (!rawRefs) return null;
    const refs = Array.isArray(rawRefs) ? rawRefs : [rawRefs];

    const errorKey = direction === "max" ? "number_error.number_max_error" : "number_error.number_min_error";

    let firstViolatedRefLabel = null;

    refs.forEach((refName) => {
      const refModel = models[refName];
      if (!refModel?.vars?.value) return;
      this.isVisible(refModel);
      if (refModel.vars.ignoreValidation) return;

      const refValue = refModel.vars.value;
      const violates = direction === "min" ? modelValue < refValue : modelValue > refValue;

      if (violates && !firstViolatedRefLabel) {
        firstViolatedRefLabel = refModel.vars.label;
      }

      if (!shouldNoBeCalledOnRef) {
        this.validateRefConstraint(refModel, models, inverseDirection, true);
      }
    });

    if (firstViolatedRefLabel !== null) {
      return this.handleError(i18n.global.t(errorKey, { fieldName: firstViolatedRefLabel }), true, model);
    }

    return this.handleError(null, false, model);
  }

  hasBadNumberInput(id) {
    return !!(id && document.getElementById(id)?.validity?.badInput);
  }

  handleError(error, isInvalid, model) {
    model.vars.error = error;
    model.vars.isInvalid = isInvalid;
    return error;
  }

  // processResponses n'écrit que sur les champs présents dans la réponse : sans ce nettoyage, un
  // champ corrigé par l'utilisateur garderait l'erreur ou l'alerte de la soumission précédente.
  clearErrors(models) {
    Object.values(models || {}).forEach((model) => {
      if (model?.vars) {
        model.vars.error = null;
        this.clearModelAlerts(model);
      }
    });
  }

  clearModelAlerts(model) {
    model.vars.isInvalid = false;
    const clearedAlerts = [];
    if (model.vars?.attr?.alerts && Object.keys(model.vars.attr.alerts).length) {
      Object.values(model.vars?.attr?.alerts).forEach((alert) => {
        alert.isTriggered = false;
        alert.fieldName = model.vars.label;
        alert.fieldId = model.vars.id;
        clearedAlerts.push(alert);
      });
    }
    return clearedAlerts;
  }
  isTrueValue(value) {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") return ["1", "true"].includes(value.trim().toLowerCase());
    return false;
  }
}

export const formValidator = new FormValidator();
